//////////////////////////////////////////////////////////////////////
// Configurations related to search                                 //
// Note: some search experience is also on defaultMapsConfig        //
// and defaultListingConfig.js is responsible public data filtering //
//////////////////////////////////////////////////////////////////////

import {
  APPEARANCE_OPTIONS,
  AUTOGRAPH_OPTIONS,
  BUSINESS_DEVELOPMENT_OPTIONS,
  CREATIVE_MEDIA_OPTIONS,
  ENDORSEMENT_OPTIONS,
  EXCLUSIVE_TRAINING_OPTIONS,
  MENTORSHIP_OPTIONS,
  SHOUTOUT_OPTIONS,
  UNLOCKABLE_CONTENT_OPTIONS,
} from '../util/constants';

// NOTE: if you want to change the structure of the data,
// you should also check src/util/configHelpers.js
// some validation is added there.

// Main search used in Topbar.
// This can be either 'keywords' or 'location'.
// Note: The mainSearch comes from the listing-search asset nowadays by default.
//       To use this built-in configuration, you need to remove the overwrite from configHelper.js (mergeSearchConfig func)
export const mainSearch = {
  searchType: 'location',
};

/**
 * Configuration for default filters.
 * These are custom configs for each filter.
 * Common properties: key, schemaType, and label.
 * Note: the order of default filters is defined in util/configHelpers.js
 * To use this built-in configuration, you need to remove the overwrite from configHelper.js (mergeSearchConfig func)
 */

export const dateRangeFilter = {
  schemaType: 'dates',
  // Should the entire date range be available, or just part of it
  // Note: Since we don't enforce location search for dates filtering,
  //       we don't use API's 'time-full' in actual queries. It would require time zone info.
  availability: 'time-full', // time-partial
  // Options: day/night. This affects counting and whether single day picking is possible.
  dateRangeMode: 'day',
};

/**
 * Note: the order of default filters is defined in util/configHelpers.js
 * To use this built-in configuration, you need to remove the overwrite from configHelper.js (mergeSearchConfig func)
 */
export const priceFilter = {
  schemaType: 'price',
  // Note: unlike most prices this is not handled in subunits
  min: 0,
  max: 1000,
  step: 5,
};
// // This is not in use by default.
// export const keywordsFilter = {
//   key: 'keywords',
//   schemaType: 'keywords',
// }

export const sortConfig = {
  // Enable/disable the sorting control in the SearchPage
  active: true,

  // Note: queryParamName 'sort' is fixed,
  // you can't change it since Marketplace API expects it to be named as 'sort'
  queryParamName: 'sort',

  // Internal key for the relevance option, see notes below.
  relevanceKey: 'relevance',

  // Relevance key is used with keywords filter.
  // Keywords filter also sorts results according to relevance.
  relevanceFilter: 'keywords',

  // Keyword filter is sorting the results by relevance.
  // If keyword filter is active, one might want to disable other sorting options
  // by adding 'keywords' to this list.
  conflictingFilters: [],

  options: [
    // These are default sort options
    { key: 'createdAt', labelTranslationKey: 'SortBy.newest' },
    { key: '-createdAt', labelTranslationKey: 'SortBy.oldest' },
    { key: '-price', labelTranslationKey: 'SortBy.lowestPrice' },
    { key: 'price', labelTranslationKey: 'SortBy.highestPrice' },
    // If you add own sort options, you can also use label key: { key: 'meta_rating', label: 'Highest rated' },

    // The relevance is only used for keyword search, but the
    // parameter isn't sent to the Marketplace API. The key is purely
    // for handling the internal state of the sorting dropdown.
    {
      key: 'relevance',
      labelTranslationKey: 'SortBy.relevance',
      labelTranslationKeyLong: 'SortBy.relevanceLong',
    },
  ],
};

export const BrandPermierFilterOptions = [
  {
    value: 'Brand Premier',
    label: 'Yes',
  },
  {
    value: 'Brand Standard',
    label: 'No',
  },
];

export const jobCategories = [
  { key: 'campaign', label: 'Campaigns' },
  { key: 'perks', label: 'Perks' },
  { key: 'bulkJobPost', label: 'Bulk Jobs' },
  { key: 'singlejobPost', label: 'Single Jobs' },
  { key: 'reward', label: 'Rewards' },
];
export const signatureServicesKeys = [
  'autographs',
  'shoutouts',
  'endorsements',
  'appearances',
  'creativeMedia',
];
export const enhancedServicesKeys = [
  'exclusiveTraining',
  'mentorship',
  'businessDevelopment',
  'contentCreation',
  'socialMedia',
  'customService',
];

export const serviceLevelOptions = [
  { value: 'complete', label: 'Fully Managed (Complete)' },
  { value: 'assist', label: 'Guided Help (Assist)' },
  { value: 'diy', label: 'Do It Yourself (DIY)' },
];
export const socialMediaFilterOptions = [
  { value: 'tiktok', label: 'TikTok' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'total', label: 'Total' },
];
export const deliveryFilterOptions = [
  { value: 'mail', label: 'Mail' },
  { value: 'inbox', label: 'Inbox' },
  { value: 'couponCode', label: 'Coupon Code' },
];
export const perksAvailabilityFilterOptions = [
  { value: 'specificCriteria', label: 'Specific Criteria' },
  { value: 'allTalent', label: 'All Talent' },
];
export const rewardAvailabilityFilterOptions = [
  { value: 'specificCriteria', label: 'Specific Criteria' },
  { value: 'anyTalent', label: 'Any Talent' },
  { value: 'allUsers', label: 'All Users' },
];

export const rewardGroupAvailabilityOptions = [
  { value: 'certain', label: 'Certain Sports' },
  { value: 'allSports', label: 'All Sports' },
  { value: 'fans', label: 'Fans' },
  { value: 'all', label: 'All' },
];
export const jobsAvailabilityFilterOptions = [
  { value: 'specificCriteria', label: 'Specific Criteria' },
  { value: 'anyTalent', label: 'Any Talent' },
];
export const groupJobAvailabilityOptions = [
  { value: 'certain', label: 'Certain Sports' },
  { value: 'all', label: 'All' },
];

export const serviceOptions = [
  {
    labelId: 'FilterCategories.signatureServices',
    defaultMessage: 'Signature Services',
    section: 'signatureServices',
    subOptions: [
      {
        value: 'isAutograph',
        labelId: 'FilterCategories.autographs',
        defaultMessage: 'Autographs',
        nestedOptions: AUTOGRAPH_OPTIONS.map(option => ({
          label: option,
          value: option,
        })),
      },
      {
        value: 'isShoutout',
        labelId: 'FilterCategories.shoutoutsOrGreetings',
        defaultMessage: 'Shoutouts or Greetings',
        nestedOptions: SHOUTOUT_OPTIONS.map(option => ({
          label: option,
          value: option,
        })),
      },
      {
        value: 'isEndorsed',
        labelId: 'FilterCategories.socialMedia',
        defaultMessage: 'Social Media',
        nestedOptions: ENDORSEMENT_OPTIONS.map(option => ({
          label: option,
          value: option,
        })),
      },
      {
        value: 'isCreativeMedia',
        labelId: 'FilterCategories.creativeMedia',
        defaultMessage: 'Creative Media',
        nestedOptions: CREATIVE_MEDIA_OPTIONS.map(option => ({
          label: option,
          value: option,
        })),
      },
      {
        value: 'isAppearance',
        labelId: 'FilterCategories.appearances',
        defaultMessage: 'Appearances',
        nestedOptions: APPEARANCE_OPTIONS.map(option => ({
          label: option,
          value: option,
        })),
      },
    ],
  },
  {
    labelId: 'FilterCategories.enhancedServices',
    defaultMessage: 'Enhanced Services',
    section: 'enhancedServices',
    subOptions: [
      {
        value: 'isExclusiveTraining',
        labelId: 'FilterCategories.exclusiveTraining',
        defaultMessage: 'Exclusive Training',
        nestedOptions: EXCLUSIVE_TRAINING_OPTIONS.map(option => ({
          label: option,
          value: option,
        })),
      },
      {
        value: 'isMentorship',
        labelId: 'FilterCategories.mentorship',
        defaultMessage: 'Mentorship',
        nestedOptions: MENTORSHIP_OPTIONS.map(option => ({
          label: option,
          value: option,
        })),
      },
      {
        value: 'isBusinessDevelopment',
        labelId: 'FilterCategories.businessDevelopment',
        defaultMessage: 'Business Development',
        nestedOptions: BUSINESS_DEVELOPMENT_OPTIONS.map(option => ({
          label: option,
          value: option,
        })),
      },
      {
        value: 'isUnlockableContent',
        labelId: 'FilterCategories.unlockableContent',
        defaultMessage: 'Unlockable Content',
        nestedOptions: UNLOCKABLE_CONTENT_OPTIONS.map(option => ({
          label: option,
          value: option,
        })),
      },
      // {
      //   value: 'isEnhancedServices',
      //   labelId: 'FilterCategories.customOptions',
      //   defaultMessage: 'Custom Options',
      // },
    ],
  },
  {
    labelId: 'FilterCategories.talentPlus',
    defaultMessage: 'Talent Plus',
    section: 'talentPlus',
    subOptions: [
      // {
      //   value: 'isTalentPlusServices',
      //   labelId: 'FilterCategories.unlockableContent',
      //   defaultMessage: 'Unlockable Content',
      //   nestedOptions: UNLOCKABLE_CONTENT_OPTIONS.map(option => ({
      //     label: option,
      //     value: option,
      //   })),
      // },
    ],
  },
  {
    labelId: 'FilterCategories.auctions',
    defaultMessage: 'Auctions',
    section: 'auctions',
    subOptions: [
      {
        value: 'auctions',
        labelId: 'FilterCategories.auctions',
        defaultMessage: 'Auctions',
      },
    ],
  },
];
